import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Package, Home, FileText, Wallet, Users, BarChart3, MapPin, Settings, LogOut, Bell, Plus, Menu, X, Gift, ArrowDownLeft, ArrowUpRight, Undo2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { verLogo, listarNotificacoes, marcarNotificacoesLidas } from '../lib/api';
import { Button } from './ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_delivery-desk-12/artifacts/76spvmi3_3D4A54DE-AC29-47B8-ABEA-5150F325A161.png';

const navItems = [
  { to: '/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/envios', icon: Package, label: 'Envios' },
  { to: '/rastreio', icon: MapPin, label: 'Rastreio' },
  { to: '/calcular', icon: FileText, label: 'Novo envio' },
  { to: '/clientes', icon: Users, label: 'Clientes' },
  { to: '/carteira', icon: Wallet, label: 'Carteira' },
];

const CATEGORIA_ICONE = {
  bonus: { icon: Gift, color: 'text-emerald-600 bg-emerald-50' },
  recarga: { icon: ArrowDownLeft, color: 'text-[#2A5BC7] bg-[#EAF1FF]' },
  envio: { icon: ArrowUpRight, color: 'text-[#F77820] bg-[#FFF1E5]' },
  estorno: { icon: Undo2, color: 'text-slate-600 bg-slate-100' },
};

const tempoRelativo = (iso) => {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h atrás`;
  return `${Math.floor(h / 24)} d atrás`;
};

const AppShell = ({ children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItens, setNotifItens] = useState([]);
  const [notifNaoLidas, setNotifNaoLidas] = useState(0);
  const [notifCarregado, setNotifCarregado] = useState(false);

  useEffect(() => {
    let url;
    if (user?.tem_logo) {
      verLogo().then((b) => { url = URL.createObjectURL(b); setLogoUrl(url); }).catch(() => setLogoUrl(null));
    } else { setLogoUrl(null); }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user?.tem_logo]);

  useEffect(() => {
    listarNotificacoes()
      .then((r) => { setNotifItens(r.itens); setNotifNaoLidas(r.nao_lidas); })
      .catch(() => {})
      .finally(() => setNotifCarregado(true));
  }, []);

  useEffect(() => {
    if (!notifOpen) return undefined;
    const fechar = (e) => { if (!e.target.closest('[data-menu-notificacoes]')) setNotifOpen(false); };
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [notifOpen]);

  const abrirNotificacoes = () => {
    const vaiAbrir = !notifOpen;
    setNotifOpen(vaiAbrir);
    if (vaiAbrir && notifNaoLidas > 0) {
      setNotifNaoLidas(0);
      marcarNotificacoesLidas().catch(() => {});
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    setTimeout(() => navigate('/login'), 0);
    return null;
  }

  const Sidebar = ({ onItemClick }) => (
    <aside className="flex flex-col h-full bg-white border-r border-slate-100">
      <div className="p-6 border-b border-slate-100">
        <Link to="/"><img src={LOGO_URL} alt="In'Nova Envios" className="h-20" /></Link>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => {
          const active = location.pathname === to;
          return (
            <Link key={to} to={to} onClick={onItemClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#EAF1FF] text-[#2A5BC7]' : 'text-slate-600 hover:bg-slate-50'}`}>
              <Icon className="w-4 h-4" /> {label}
            </Link>
          );
        })}
        <Link to="/relatorios" onClick={onItemClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/relatorios' ? 'bg-[#EAF1FF] text-[#2A5BC7]' : 'text-slate-600 hover:bg-slate-50'}`}>
          <BarChart3 className="w-4 h-4" /> Relatórios
        </Link>
        <Link to="/configuracoes" onClick={onItemClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/configuracoes' ? 'bg-[#EAF1FF] text-[#2A5BC7]' : 'text-slate-600 hover:bg-slate-50'}`}>
          <Settings className="w-4 h-4" /> Configurações
        </Link>
      </nav>
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 p-2">
          <Link to="/perfil" onClick={onItemClick} className="flex items-center gap-3 flex-1 min-w-0 rounded-lg hover:bg-slate-50 transition-colors -m-1 p-1" title="Meu perfil">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-9 h-9 rounded-full object-contain bg-white border border-slate-200 flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2A5BC7] to-[#F77820] flex items-center justify-center text-white font-semibold flex-shrink-0">
                {user.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          </Link>
          <button onClick={handleLogout} className="text-slate-400 hover:text-[#F77820]" title="Sair"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex fixed left-0 top-0 h-screen w-64 z-10">
        <Sidebar />
      </div>

      {/* Mobile sidebar drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white">
            <Sidebar onItemClick={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Imagem de fundo da área de conteúdo, bem clara (marca d'água) */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 lg:left-64 z-0 bg-no-repeat bg-cover bg-[70%_center] opacity-[0.13]"
        style={{ backgroundImage: "url(/img/fundo-envios.webp)" }} />

      <div className="lg:ml-64 relative z-[1]">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-5 lg:px-8 h-16 gap-3">
            <button className="lg:hidden p-2 text-slate-700" onClick={() => setOpen(true)} aria-label="Menu"><Menu className="w-5 h-5" /></button>
            <div className="hidden lg:block">
              <h1 className="font-bold text-lg text-slate-900">{title || `Olá, ${user.name?.split(' ')[0] || ''}`}</h1>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <Link to="/carteira" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EAF1FF] text-[#2A5BC7] hover:bg-[#dbe6fd] transition-colors text-sm font-semibold">
                <Wallet className="w-4 h-4" /> R$ {(user.saldo || 0).toFixed(2).replace('.', ',')}
              </Link>
              <span className="relative" data-menu-notificacoes>
                <button className="relative p-2 text-slate-600 hover:text-[#2A5BC7]" onClick={abrirNotificacoes} aria-label="Notificações">
                  <Bell className="w-5 h-5" />
                  {notifNaoLidas > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#F77820] rounded-full" />}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-11 z-20 w-80 max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
                    <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-900">Notificações</div>
                    {!notifCarregado ? (
                      <p className="px-4 py-6 text-sm text-slate-400 text-center">Carregando...</p>
                    ) : notifItens.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-slate-400 text-center">Nenhuma notificação ainda</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {notifItens.map((n) => {
                          const cat = CATEGORIA_ICONE[n.categoria] || { icon: Bell, color: 'text-slate-600 bg-slate-100' };
                          const Icon = cat.icon;
                          const sinal = n.tipo === 'credito' ? '+' : '−';
                          return (
                            <li key={n.id} className={`px-4 py-3 flex gap-3 ${!n.lida ? 'bg-[#EAF1FF]/40' : ''}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cat.color}`}><Icon className="w-4 h-4" /></div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-slate-800 leading-snug">{n.descricao}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{tempoRelativo(n.created_at)} · {sinal} R$ {Number(n.valor).toFixed(2).replace('.', ',')}</p>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <Link to="/carteira" onClick={() => setNotifOpen(false)} className="block text-center text-xs font-semibold text-[#2A5BC7] hover:underline py-2.5 border-t border-slate-100">Ver extrato completo</Link>
                  </div>
                )}
              </span>
              <Link to="/calcular">
                <Button className="inn-btn-primary rounded-full h-10 px-4 sm:px-5">
                  <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Novo envio</span>
                </Button>
              </Link>
            </div>
          </div>
        </header>
        <main className="p-5 lg:p-8 space-y-6">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
